import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
// Ruta `compat` (flarum.core.compat['tags/components/TagLinkButton']): es la unica que
// flarum-webpack-config sabe resolver hacia otra extension sin declarar `useExtensions`, y
// `flarum/tags` no exporta componentes por el API moderno (su forum/index.ts solo reexporta
// `extend`). Funciona porque el bundle de tags se evalua antes que el nuestro — lo garantiza
// el `require: flarum/tags` del composer.json, que fija el orden de arranque.
import TagLinkButton from 'flarum/tags/components/TagLinkButton';

const STORAGE_KEY = 'umbral.comunidad.sidebarCollapsed';
const HTML_CLASS = 'cm-side-collapsed';

export function isCollapsed(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch (e) {
    // Safari en modo privado y algunos navegadores con cookies bloqueadas tiran aqui.
    // Sin persistencia el panel simplemente arranca expandido.
    return false;
  }
}

function applyClass(collapsed: boolean) {
  document.documentElement.classList.toggle(HTML_CLASS, collapsed);
}

function setCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  } catch (e) {
    // Ver isCollapsed(): sin storage el colapso sigue funcionando, solo no sobrevive al reload.
  }

  applyClass(collapsed);
}

/**
 * (c) Sidebar colapsable con estado persistente (Spec §9).
 *
 * El estado se marca en <html> y no en el vnode del sidebar: ese `<nav class="IndexPage-nav
 * sideNav">` lo rinde `IndexPage.view` del core, y envolverlo exigiria cirugia de vnodes que
 * se rompe en cada upgrade. Con una clase en la raiz, todo el colapso es CSS (less §8b) y el
 * unico JS es el toggle + la restauracion desde localStorage.
 *
 * Se aplica la clase ya en el arranque (antes del primer render) para que no haya parpadeo
 * expandido→colapsado al recargar.
 */
export default function collapsibleSidebar() {
  applyClass(isCollapsed());

  extend(IndexPage.prototype, 'sidebarItems', function (items: any) {
    // Orden buscado: [Nueva discusion] [⟨/⟩] / [lista de categorias].
    // `newDiscussion` y `nav` entran con prioridad 0, y `toArray()` ordena por prioridad
    // descendente conservando el orden de insercion entre iguales — asi que un item nuevo
    // caeria SIEMPRE al final. Bajar `nav` es la forma de meterse en medio sin reordenar
    // items de terceros.
    if (items.has('nav')) {
      items.setPriority('nav', -10);
    }

    const collapsed = isCollapsed();
    const label = app.translator.trans(
      collapsed ? 'umbral-comunidad.forum.sidebar.expand' : 'umbral-comunidad.forum.sidebar.collapse'
    );

    items.add(
      'umbralCollapse',
      <button
        type="button"
        className="cm-collapse-btn"
        data-cm-collapse="1"
        aria-pressed={collapsed ? 'true' : 'false'}
        title={label}
        aria-label={label}
        onclick={() => {
          setCollapsed(!isCollapsed());
          m.redraw();
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <line x1="9" y1="4" x2="9" y2="20" />
        </svg>
      </button>,
      0
    );
  });

  // Contadores + tooltips en la lista de categorias del sidebar (mockup D2: `<small>43</small>`;
  // Spec §9: colapsado se ve "solo iconos con tooltip").
  //
  // Se parchea la VISTA del componente, no la ItemList de `navItems`. Motivo verificado en el
  // DOM del staging: `TagLinkButton.view()` ignora por completo `vnode.children` y se rinde el
  // `<span class="Button-label">` a mano con `tag.name()`, asi que mutar los children del item
  // compila, no rompe nada... y no pinta nada.
  extend(TagLinkButton.prototype, 'view', function (this: any, vnode: any) {
    const tag: any = this.attrs?.model;

    if (!tag || !vnode?.attrs || !Array.isArray(vnode.children)) return;

    // Colapsado solo quedan los iconos: el tooltip tiene que llevar el nombre. `TagLinkButton`
    // pone ahi la descripcion, que se conserva detras del nombre.
    const description = typeof tag.description === 'function' ? tag.description() : null;
    vnode.attrs.title = description ? tag.name() + ' · ' + description : tag.name();

    const count = typeof tag.discussionCount === 'function' ? tag.discussionCount() : null;

    if (typeof count !== 'number') return;

    vnode.children.push(<span className="cm-side-count">{count}</span>);
  });
}
